import type { MembersAreaDetail, MembersLesson } from './members-area.contract'

/** Cursos locais de demonstração que aparecem liberados na prévia, com o percentual do card. */
const DEMO_SHELF_PROGRESS: Record<string, number> = {
  'demo-dutra-shelf-02': 18,
  'demo-dutra-shelf-04': 36,
  'demo-dutra-shelf-06': 52,
  'demo-dutra-shelf-08': 71,
  'demo-dutra-shelf-10': 88,
}

const DEMO_SHELF_ANCHOR = 'demo-dutra-prod-mentoria'

export function previewShelfProductId(productId: string) {
  return productId.startsWith('demo-dutra-shelf-') ? DEMO_SHELF_ANCHOR : productId
}

export function previewCatalogAccess(productId: string, anchorProductId: string) {
  if (productId === anchorProductId) return { accessStatus: 'ACTIVE' as const, progressPercent: 64 }
  const progress = DEMO_SHELF_PROGRESS[productId]
  if (progress !== undefined) return { accessStatus: 'ACTIVE' as const, progressPercent: progress }
  return { accessStatus: 'LOCKED' as const, progressPercent: 0 }
}

export function toMemberPreview(detail: MembersAreaDetail, producerName: string) {
  const lessons = detail.modules.flatMap((module) => module.lessons)
  const first = lessons[0]
  const files = detail.assets.filter((asset) => asset.lessonId === null)
  const readyFiles = files.filter((asset) => asset.status === 'READY')
  return {
    draft: detail.status !== 'PUBLISHED',
    product: {
      productId: detail.productId,
      name: detail.name,
      coverImage: detail.coverImage,
      kind: detail.kind,
      continueLessonId: first?.id ?? null,
      continueLessonTitle: first?.title ?? null,
      fileCount: readyFiles.length,
      totalSizeBytes: readyFiles.reduce((sum, asset) => sum + asset.sizeBytes, 0),
    },
    producerName,
    modules: detail.modules.map((module) => ({
      id: module.id,
      title: module.title,
      lessons: module.lessons.map((lesson) => ({
        id: lesson.id,
        title: lesson.title,
        kind: lesson.kind,
        locked: false,
        availableAt: null,
        completed: false,
      })),
    })),
    files: files.map((asset) => ({
      id: asset.id,
      name: asset.name,
      sizeBytes: asset.sizeBytes,
      contentType: asset.contentType,
      ready: asset.status === 'READY',
    })),
  }
}

export function toMemberPreviewLesson(
  detail: MembersAreaDetail,
  lessonId: string,
  producerName: string,
) {
  const course = toMemberPreview(detail, producerName)
  const source = detail.modules.flatMap((module) => module.lessons).find((item) => item.id === lessonId)
  const flat = course.modules.flatMap((module) => module.lessons)
  const index = flat.findIndex((item) => item.id === lessonId)
  return {
    course,
    lesson: source === undefined ? undefined : previewLesson(source, flat[index]),
    previousLessonId: flat[index - 1]?.id ?? null,
    nextLessonId: flat[index + 1]?.id ?? null,
  }
}

function previewLesson(
  source: MembersLesson,
  summary: { id: string; title: string; kind: MembersLesson['kind']; locked: boolean; availableAt: null; completed: boolean } | undefined,
) {
  return {
    id: source.id,
    title: source.title,
    kind: source.kind,
    locked: summary?.locked ?? false,
    availableAt: summary?.availableAt ?? null,
    completed: summary?.completed ?? false,
    body: source.body,
    playbackUrl: source.video?.status === 'READY' ? source.video.playbackUrl : null,
    assets: source.assets
      .filter((asset) => asset.status === 'READY')
      .map((asset) => ({
        id: asset.id,
        name: asset.name,
        sizeBytes: asset.sizeBytes,
        contentType: asset.contentType,
      })),
  }
}
